export default {
  en: {
    title: 'Data classification',
    subtitle: 'Consolidated inventory of every personal data your account holds: the fields your forms and surveys collect, and everything Videsk collects on its own (call comments, device and location metadata, recordings, transcriptions, contacts, agent accounts and security logs), with how each item is classified under the data protection regulation that applies to you.',
    alert: 'Fields without a classification are potential compliance gaps: review and classify them.',
    platformAlert: 'Data collected by the platform comes with a default classification. Review the ones that depend on your operation: free text, recordings and typification can contain any category of data.',
    summary: {
      fields: 'Data points',
      sensitive: 'Sensitive',
      unclassified: 'Unclassified',
      documentsWithSensitive: 'Forms/surveys with sensitive data',
      declared: 'Declared by you',
      platform: 'Collected by Videsk',
    },
    filters: {
      all: 'All',
      sensitive: 'Sensitive',
      unclassified: 'Unclassified',
    },
    origins: {
      all: 'Every origin',
      declared: 'Declared',
      platform: 'Platform',
    },
    originTooltip: {
      declared: 'Field you configured in a form or a survey. You classify it.',
      platform: 'Data Videsk collects on its own, with a default classification.',
    },
    sources: {
      form: 'Form',
      survey: 'Survey',
      call: 'Call',
      metadata: 'Metadata',
      recording: 'Recordings',
      transcription: 'Transcriptions',
      appointment: 'Appointments',
      contact: 'Contacts (CRM)',
      tracking: 'Website tracking',
      room: 'Room logs',
      ai: 'AI analysis',
      integration: 'Integrations',
      user: 'Agent accounts',
      security: 'Access and security',
    },
    columns: {
      origin: 'Origin',
      source: 'Source',
      document: 'Group / Form',
      field: 'Data point',
      type: 'Type',
      mapping: 'microCRM mapping',
      classification: 'Classification',
    },
    unclassified: 'Unclassified',
    unmapped: 'Not mapped',
    notApplicable: '—',
    sensitiveTag: 'Sensitive',
    generalTag: 'General',
    nonPersonalTag: 'Non-personal',
    optionalTag: 'Optional',
    optionalTooltip: 'Only collected when you enable the feature or the customer grants the permission.',
    empty: 'No data points to show.',
    export: {
      button: 'Export XLSX',
      fileName: 'data-classification',
      fieldsSheet: 'Data points',
      summarySheet: 'Summary',
      sensitivity: 'Sensitivity',
      origin: 'Origin',
      optional: 'Optional',
      description: 'Notes',
      yes: 'Yes',
      no: 'No',
      metric: 'Metric',
      value: 'Value',
      error: 'Could not generate the file.',
    },
  },
  es: {
    title: 'Clasificación de datos',
    subtitle: 'Inventario consolidado de todos los datos personales que guarda tu cuenta: los campos que recolectan tus formularios y encuestas, y todo lo que Videsk recolecta por sí solo (comentarios de la llamada, metadata de dispositivo y ubicación, grabaciones, transcripciones, contactos, cuentas de agente y logs de seguridad), con la clasificación de cada uno según la normativa de protección de datos que te aplique.',
    alert: 'Los campos sin clasificar son posibles brechas de cumplimiento: revísalos y clasifícalos.',
    platformAlert: 'Los datos que recolecta la plataforma vienen con una clasificación por defecto. Revisa los que dependen de tu operación: el texto libre, las grabaciones y la tipificación pueden contener cualquier categoría de dato.',
    summary: {
      fields: 'Datos recolectados',
      sensitive: 'Sensibles',
      unclassified: 'Sin clasificar',
      documentsWithSensitive: 'Formularios/encuestas con datos sensibles',
      declared: 'Declarados por ti',
      platform: 'Recolectados por Videsk',
    },
    filters: {
      all: 'Todos',
      sensitive: 'Sensibles',
      unclassified: 'Sin clasificar',
    },
    origins: {
      all: 'Todo origen',
      declared: 'Declarados',
      platform: 'Plataforma',
    },
    originTooltip: {
      declared: 'Campo que configuraste en un formulario o encuesta. Tú lo clasificas.',
      platform: 'Dato que Videsk recolecta por sí solo, con una clasificación por defecto.',
    },
    sources: {
      form: 'Formulario',
      survey: 'Encuesta',
      call: 'Llamada',
      metadata: 'Metadata',
      recording: 'Grabaciones',
      transcription: 'Transcripciones',
      appointment: 'Citas',
      contact: 'Contactos (CRM)',
      tracking: 'Tracking web',
      room: 'Logs de sala',
      ai: 'Análisis IA',
      integration: 'Integraciones',
      user: 'Cuentas de agente',
      security: 'Acceso y seguridad',
    },
    columns: {
      origin: 'Origen',
      source: 'Fuente',
      document: 'Grupo / Formulario',
      field: 'Dato',
      type: 'Tipo',
      mapping: 'Mapeo microCRM',
      classification: 'Clasificación',
    },
    unclassified: 'Sin clasificar',
    unmapped: 'Sin mapear',
    notApplicable: '—',
    sensitiveTag: 'Sensible',
    generalTag: 'General',
    nonPersonalTag: 'No personal',
    optionalTag: 'Opcional',
    optionalTooltip: 'Solo se recolecta si activas la función o el cliente otorga el permiso.',
    empty: 'No hay datos para mostrar.',
    export: {
      button: 'Exportar XLSX',
      fileName: 'clasificacion-datos',
      fieldsSheet: 'Datos',
      summarySheet: 'Resumen',
      sensitivity: 'Sensibilidad',
      origin: 'Origen',
      optional: 'Opcional',
      description: 'Notas',
      yes: 'Sí',
      no: 'No',
      metric: 'Métrica',
      value: 'Valor',
      error: 'No se pudo generar el archivo.',
    },
  },
  fr: {
    title: 'Classification des données',
    subtitle: 'Inventaire consolidé de toutes les données personnelles détenues par votre compte : les champs collectés par vos formulaires et enquêtes, et tout ce que Videsk collecte de lui-même (commentaires d\'appel, métadonnées d\'appareil et de localisation, enregistrements, transcriptions, contacts, comptes agents et journaux de sécurité), avec la classification de chaque élément selon la réglementation de protection des données qui vous est applicable.',
    alert: 'Les champs sans classification sont des lacunes potentielles de conformité : examinez-les et classez-les.',
    platformAlert: 'Les données collectées par la plateforme ont une classification par défaut. Vérifiez celles qui dépendent de votre activité : le texte libre, les enregistrements et la qualification peuvent contenir n\'importe quelle catégorie de données.',
    summary: {
      fields: 'Données collectées',
      sensitive: 'Sensibles',
      unclassified: 'Non classées',
      documentsWithSensitive: 'Formulaires/enquêtes avec données sensibles',
      declared: 'Déclarées par vous',
      platform: 'Collectées par Videsk',
    },
    filters: {
      all: 'Toutes',
      sensitive: 'Sensibles',
      unclassified: 'Non classées',
    },
    origins: {
      all: 'Toute origine',
      declared: 'Déclarées',
      platform: 'Plateforme',
    },
    originTooltip: {
      declared: 'Champ que vous avez configuré dans un formulaire ou une enquête. Vous le classez.',
      platform: 'Donnée que Videsk collecte de lui-même, avec une classification par défaut.',
    },
    sources: {
      form: 'Formulaire',
      survey: 'Enquête',
      call: 'Appel',
      metadata: 'Métadonnées',
      recording: 'Enregistrements',
      transcription: 'Transcriptions',
      appointment: 'Rendez-vous',
      contact: 'Contacts (CRM)',
      tracking: 'Suivi web',
      room: 'Journaux de salle',
      ai: 'Analyse IA',
      integration: 'Intégrations',
      user: 'Comptes agents',
      security: 'Accès et sécurité',
    },
    columns: {
      origin: 'Origine',
      source: 'Source',
      document: 'Groupe / Formulaire',
      field: 'Donnée',
      type: 'Type',
      mapping: 'Mappage microCRM',
      classification: 'Classification',
    },
    unclassified: 'Non classé',
    unmapped: 'Non mappé',
    notApplicable: '—',
    sensitiveTag: 'Sensible',
    generalTag: 'Général',
    nonPersonalTag: 'Non personnelle',
    optionalTag: 'Optionnel',
    optionalTooltip: 'Collecté uniquement si vous activez la fonctionnalité ou si le client accorde la permission.',
    empty: 'Aucune donnée à afficher.',
    export: {
      button: 'Exporter XLSX',
      fileName: 'classification-donnees',
      fieldsSheet: 'Données',
      summarySheet: 'Résumé',
      sensitivity: 'Sensibilité',
      origin: 'Origine',
      optional: 'Optionnel',
      description: 'Notes',
      yes: 'Oui',
      no: 'Non',
      metric: 'Métrique',
      value: 'Valeur',
      error: 'Impossible de générer le fichier.',
    },
  },
};
